import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

type Tab = 'leads' | 'referrals' | 'loyalty' | 'sweepstakes';

const TABS: { id: Tab; label: string }[] = [
  { id: 'leads', label: 'Leads redes' },
  { id: 'referrals', label: 'Referidos' },
  { id: 'loyalty', label: 'Puntos' },
  { id: 'sweepstakes', label: 'Sorteos' },
];

const LEAD_STATUS: Record<string, string> = {
  NEW: 'Nuevo',
  CONTACTED: 'Contactado',
  QUALIFIED: 'Calificado',
  CONVERTED: 'Convertido',
  DISCARDED: 'Descartado',
};

export function CrmMarketing() {
  const [tab, setTab] = useState<Tab>('leads');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [leads, setLeads] = useState<any[]>([]);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [promotions, setPromotions] = useState<any[]>([]);
  const [loyaltyAccounts, setLoyaltyAccounts] = useState<any[]>([]);
  const [sweepstakes, setSweepstakes] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);

  const [leadForm, setLeadForm] = useState({
    firstName: '',
    lastName: '',
    documentNumber: '',
    cellphone: '',
    zone: '',
    observations: '',
  });
  const [referralForm, setReferralForm] = useState({
    referrerCustomerId: '',
    referredDocumentNumber: '',
    promotionCode: 'SUPERAMIGO',
  });
  const [sweepForm, setSweepForm] = useState({
    name: '',
    description: '',
    endDate: '',
    pointsPerEntry: '20',
  });
  const [loyaltyCustomers, setLoyaltyCustomers] = useState<any[]>([]);
  const [selectedLoyaltyCustomer, setSelectedLoyaltyCustomer] = useState('');
  const [loyaltyDetail, setLoyaltyDetail] = useState<any | null>(null);
  const [loyaltyForm, setLoyaltyForm] = useState({ points: '', notes: '' });
  const [loyaltyBusy, setLoyaltyBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'leads') setLeads(await apiService.listSocialLeads());
      else if (tab === 'referrals') {
        const [refs, promos, custs] = await Promise.all([
          apiService.listCustomerReferrals(),
          apiService.listReferralPromotions(),
          apiService.getCustomers(),
        ]);
        setReferrals(refs);
        setPromotions(promos);
        setCustomers(custs);
      } else if (tab === 'loyalty') {
        const [accounts, custs] = await Promise.all([
          apiService.listLoyaltyAccounts(),
          apiService.getCustomers(),
        ]);
        setLoyaltyAccounts(accounts);
        setLoyaltyCustomers(custs);
      } else if (tab === 'sweepstakes') {
        const [sw, custs] = await Promise.all([
          apiService.listSweepstakes(),
          apiService.getCustomers(),
        ]);
        setSweepstakes(sw);
        setCustomers(custs);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tab]);

  const createLead = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.createSocialLead(leadForm);
      setLeadForm({ firstName: '', lastName: '', documentNumber: '', cellphone: '', zone: '', observations: '' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const convertLead = async (id: string) => {
    try {
      await apiService.convertSocialLead(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const registerReferral = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.registerCustomerReferral(referralForm);
      setReferralForm({ referrerCustomerId: '', referredDocumentNumber: '', promotionCode: 'SUPERAMIGO' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const createSweep = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.createSweepstakes({
        name: sweepForm.name,
        description: sweepForm.description,
        startDate: new Date().toISOString().slice(0, 10),
        endDate: sweepForm.endDate,
        pointsPerEntry: parseInt(sweepForm.pointsPerEntry, 10),
      });
      setSweepForm({ name: '', description: '', endDate: '', pointsPerEntry: '20' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const drawSweep = async (id: string) => {
    try {
      await apiService.drawSweepstakes(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const enterSweep = async (sweepId: string, customerId: string) => {
    try {
      await apiService.enterSweepstakes(sweepId, customerId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const loadLoyaltyDetail = async (customerId: string) => {
    setSelectedLoyaltyCustomer(customerId);
    if (!customerId) {
      setLoyaltyDetail(null);
      return;
    }
    try {
      setLoyaltyDetail(await apiService.getLoyaltyAccount(customerId));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const adjustLoyalty = async (sign: 1 | -1) => {
    if (!selectedLoyaltyCustomer || !loyaltyForm.points) return;
    setLoyaltyBusy(true);
    setError(null);
    try {
      const points = parseInt(loyaltyForm.points, 10) * sign;
      await apiService.adjustLoyaltyPoints({
        customerId: selectedLoyaltyCustomer,
        points,
        notes: loyaltyForm.notes || (sign > 0 ? 'Ajuste manual +' : 'Ajuste manual -'),
      });
      setLoyaltyForm({ points: '', notes: '' });
      await load();
      await loadLoyaltyDetail(selectedLoyaltyCustomer);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoyaltyBusy(false);
    }
  };

  const redeemLoyalty = async () => {
    if (!selectedLoyaltyCustomer || !loyaltyForm.points) return;
    setLoyaltyBusy(true);
    setError(null);
    try {
      await apiService.redeemLoyaltyPoints({
        customerId: selectedLoyaltyCustomer,
        points: parseInt(loyaltyForm.points, 10),
        notes: loyaltyForm.notes || 'Canje de puntos',
      });
      setLoyaltyForm({ points: '', notes: '' });
      await load();
      await loadLoyaltyDetail(selectedLoyaltyCustomer);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoyaltyBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">CRM y marketing</h1>
        <p className="text-sm text-gray-500 mt-1">Leads, referidos, puntos y sorteos</p>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm rounded-md ${tab === t.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : (
        <>
          {tab === 'leads' && (
            <div className="space-y-4">
              <form onSubmit={createLead} className="grid md:grid-cols-3 gap-3 bg-white border p-4 rounded-lg">
                <input placeholder="Nombre" value={leadForm.firstName} onChange={(e) => setLeadForm({ ...leadForm, firstName: e.target.value })} className="px-2 py-1 border rounded" />
                <input placeholder="Apellido" value={leadForm.lastName} onChange={(e) => setLeadForm({ ...leadForm, lastName: e.target.value })} className="px-2 py-1 border rounded" />
                <input placeholder="DNI" value={leadForm.documentNumber} onChange={(e) => setLeadForm({ ...leadForm, documentNumber: e.target.value })} className="px-2 py-1 border rounded" />
                <input placeholder="Celular" value={leadForm.cellphone} onChange={(e) => setLeadForm({ ...leadForm, cellphone: e.target.value })} className="px-2 py-1 border rounded" />
                <input placeholder="Zona" value={leadForm.zone} onChange={(e) => setLeadForm({ ...leadForm, zone: e.target.value })} className="px-2 py-1 border rounded" />
                <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm">+ Lead</button>
              </form>
              <table className="min-w-full text-sm bg-white border rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Contacto</th>
                    <th className="px-3 py-2 text-left">DNI</th>
                    <th className="px-3 py-2 text-left">Zona</th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t">
                      <td className="px-3 py-2">{l.firstName} {l.lastName}<span className="text-gray-400 block text-xs">{l.cellphone}</span></td>
                      <td className="px-3 py-2">{l.documentNumber ?? '—'}</td>
                      <td className="px-3 py-2">{l.zone ?? '—'}</td>
                      <td className="px-3 py-2">{LEAD_STATUS[l.status] ?? l.status}</td>
                      <td className="px-3 py-2">
                        {l.status !== 'CONVERTED' && l.documentNumber && (
                          <button onClick={() => convertLead(l.id)} className="text-blue-600 hover:underline text-xs">→ Presolicitud</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'referrals' && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="font-medium mb-2">Promociones activas</h3>
                  {promotions.map((p) => (
                    <p key={p.id} className="text-sm text-gray-700">
                      <span className="font-mono">{p.code}</span> — {p.name} ({p.referrerPoints} pts)
                    </p>
                  ))}
                </div>
                <form onSubmit={registerReferral} className="bg-white border rounded-lg p-4 space-y-2">
                  <h3 className="font-medium">Registrar referido</h3>
                  <select required value={referralForm.referrerCustomerId} onChange={(e) => setReferralForm({ ...referralForm, referrerCustomerId: e.target.value })} className="w-full px-2 py-1 border rounded text-sm">
                    <option value="">Cliente que refiere</option>
                    {customers.map((c: any) => (
                      <option key={c.id} value={c.id}>{c.firstName} {c.lastName} ({c.documentNumber})</option>
                    ))}
                  </select>
                  <input required placeholder="DNI del referido" value={referralForm.referredDocumentNumber} onChange={(e) => setReferralForm({ ...referralForm, referredDocumentNumber: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" />
                  <input placeholder="Código promo" value={referralForm.promotionCode} onChange={(e) => setReferralForm({ ...referralForm, promotionCode: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" />
                  <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm">Registrar</button>
                </form>
              </div>
              <table className="min-w-full text-sm bg-white border rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2">Referidor</th>
                    <th className="px-3 py-2">DNI referido</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Puntos</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-3 py-2">{r.referrerCustomer?.firstName} {r.referrerCustomer?.lastName}</td>
                      <td className="px-3 py-2">{r.referredDocumentNumber}</td>
                      <td className="px-3 py-2">{r.status}</td>
                      <td className="px-3 py-2">{r.referrerPointsAwarded ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'loyalty' && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white border rounded-lg p-4 space-y-3">
                  <h3 className="font-medium">Gestionar puntos</h3>
                  <select
                    value={selectedLoyaltyCustomer}
                    onChange={(e) => loadLoyaltyDetail(e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="">Seleccionar cliente…</option>
                    {loyaltyCustomers.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.firstName} {c.lastName} ({c.documentNumber})
                      </option>
                    ))}
                  </select>
                  {loyaltyDetail && (
                    <p className="text-sm">
                      Saldo: <strong>{loyaltyDetail.balance}</strong> pts · Acumulado: {loyaltyDetail.lifetimeEarned}
                    </p>
                  )}
                  <input
                    type="number"
                    placeholder="Puntos"
                    value={loyaltyForm.points}
                    onChange={(e) => setLoyaltyForm({ ...loyaltyForm, points: e.target.value })}
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                  <input
                    placeholder="Notas"
                    value={loyaltyForm.notes}
                    onChange={(e) => setLoyaltyForm({ ...loyaltyForm, notes: e.target.value })}
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={loyaltyBusy}
                      onClick={() => adjustLoyalty(1)}
                      className="px-3 py-1.5 bg-green-600 text-white rounded text-sm disabled:opacity-50"
                    >
                      + Acreditar
                    </button>
                    <button
                      type="button"
                      disabled={loyaltyBusy}
                      onClick={() => adjustLoyalty(-1)}
                      className="px-3 py-1.5 bg-amber-600 text-white rounded text-sm disabled:opacity-50"
                    >
                      − Debitar
                    </button>
                    <button
                      type="button"
                      disabled={loyaltyBusy}
                      onClick={redeemLoyalty}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
                    >
                      Canjear
                    </button>
                  </div>
                </div>
                {loyaltyDetail?.transactions?.length > 0 && (
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="font-medium mb-2">Últimos movimientos</h3>
                    <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
                      {loyaltyDetail.transactions.map((t: any) => (
                        <li key={t.id} className="flex justify-between border-b py-1">
                          <span>{t.type} · {t.notes ?? '—'}</span>
                          <span className={t.points >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {t.points > 0 ? '+' : ''}{t.points}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <table className="min-w-full text-sm bg-white border rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Cliente</th>
                    <th className="px-3 py-2 text-right">Saldo</th>
                    <th className="px-3 py-2 text-right">Acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {loyaltyAccounts.map((a) => (
                    <tr
                      key={a.id}
                      className="border-t cursor-pointer hover:bg-gray-50"
                      onClick={() => loadLoyaltyDetail(a.customer?.id ?? a.customerId)}
                    >
                      <td className="px-3 py-2">{a.customer?.firstName} {a.customer?.lastName}</td>
                      <td className="px-3 py-2 text-right font-semibold">{a.balance}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{a.lifetimeEarned}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'sweepstakes' && (
            <div className="space-y-4">
              <form onSubmit={createSweep} className="grid md:grid-cols-4 gap-2 bg-white border p-4 rounded-lg">
                <input required placeholder="Nombre sorteo" value={sweepForm.name} onChange={(e) => setSweepForm({ ...sweepForm, name: e.target.value })} className="px-2 py-1 border rounded" />
                <input type="date" required value={sweepForm.endDate} onChange={(e) => setSweepForm({ ...sweepForm, endDate: e.target.value })} className="px-2 py-1 border rounded" />
                <input type="number" placeholder="Pts/entrada" value={sweepForm.pointsPerEntry} onChange={(e) => setSweepForm({ ...sweepForm, pointsPerEntry: e.target.value })} className="px-2 py-1 border rounded" />
                <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm">Crear sorteo</button>
              </form>
              {sweepstakes.map((s) => (
                <div key={s.id} className="bg-white border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{s.name}</h3>
                      <p className="text-xs text-gray-500">{s.status} · {s.entries?.length ?? 0} participaciones · {s.pointsPerEntry} pts/entrada</p>
                      {s.winnerCustomer && (
                        <p className="text-sm text-green-700 mt-1">Ganador: {s.winnerCustomer.firstName} {s.winnerCustomer.lastName}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {s.status === 'ACTIVE' && (
                        <>
                          <select
                            className="text-xs border rounded px-2 py-1"
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) enterSweep(s.id, e.target.value);
                              e.target.value = '';
                            }}
                          >
                            <option value="">Participar cliente…</option>
                            {customers.map((c: any) => (
                              <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                            ))}
                          </select>
                          <button onClick={() => drawSweep(s.id)} className="text-xs text-indigo-600 hover:underline">Sortear</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
